import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { api } from "../api";
import { CrudPage, type Field, type Column } from "../components/CrudPage";
import { ErrorNote } from "../components/Page";

/**
 * The Leads tab, once per section.
 *
 * The same record and the same pipeline either way, but not the same work: a
 * demand lead ends in a contract and a supply lead ends in someone on the
 * roster. Keeping them in their own sections means a glance never starts by
 * filtering, and the column worth showing differs too -- what industry a
 * prospect is in, versus what town a cleaner covers.
 */

const STATUSES = [
  "NEW",
  "REVIEWED",
  "CONTACTED",
  "NEGOTIATING",
  "WON",
  "LOST",
  "DISQUALIFIED",
] as const;

const RATE_UNITS = ["hour", "visit", "month", "sqft"] as const;

/** Shared by both screens; `side` is set by the screen, not chosen in the form. */
const commonFields: Field[] = [
  { name: "name", label: "Name", type: "text", required: true },
  { name: "status", label: "Status", type: "select", required: true, options: STATUSES },
  {
    name: "source",
    label: "Source",
    type: "text",
    required: true,
    placeholder: "manual, kijiji, places…",
  },
  { name: "email", label: "Email", type: "email" },
  { name: "phone", label: "Phone", type: "tel" },
  { name: "website", label: "Website", type: "url", placeholder: "https://…" },
  { name: "address", label: "Address", type: "text" },
  { name: "sourceUrl", label: "Source URL", type: "url", placeholder: "https://…" },
  { name: "pitch", label: "Pitch", type: "textarea", placeholder: "One-line angle for outreach" },
  {
    name: "description",
    label: "Their ad / the notice",
    type: "textarea",
    help: "What the source actually said, kept verbatim",
  },
];

const clientFields: Field[] = [
  ...commonFields.slice(0, 3),
  { name: "industry", label: "Industry", type: "text" },
  ...commonFields.slice(3),
];

type Record_ = Record<string, unknown>;

export function ClientLeads() {
  return <LeadsScreen side="demand" />;
}

export function CleanerLeads() {
  return <LeadsScreen side="supply" />;
}

function LeadsScreen({ side }: { side: "demand" | "supply" }) {
  const [converting, setConverting] = useState<Record_ | null>(null);
  const demand = side === "demand";

  const columns: Column[] = [
    { name: "name", label: "Name" },
    // Location earns the second slot on both sides -- it is the first thing that
    // decides whether a job is worth driving to, and on a phone only the first
    // two columns are shown.
    { name: "address", label: demand ? "Location" : "Area" },
    // Distance from Huntsville, which is what actually decides whether a job is
    // worth taking. Sorted on by default rather than merely shown: the list is
    // longer than anyone will work through, so the order is the whole point.
    { name: "distanceKm", label: "km away" },
    // Supply only: whether the ad was written by a person or a firm. Hunt's
    // subcontracts to individuals, so an established company is a competitor.
    ...(demand
      ? []
      : [
          {
            name: "advertiserType",
            label: "Advertiser",
            labels: { individual: "Individual", company: "Company", unknown: "Unclear" },
            fallback: "unknown",
          } as Column,
        ]),
    ...(demand ? [{ name: "industry", label: "Industry" }] : []),
    { name: "status", label: "Status", pill: true },
    { name: "source", label: "Source" },
    { name: "phone", label: "Phone" },
    { name: "createdAt", label: "Added", date: true },
  ];

  return (
    <>
      <CrudPage
        resource="leads"
        title="Leads"
        fields={demand ? clientFields : commonFields}
        columns={columns}
        statusField="status"
        statuses={STATUSES}
        where={{ side }}
        defaultSort={{ column: "distanceKm" }}
        secondaryFilter={
          demand
            ? {
                field: "serviceLine",
                allLabel: "All service lines",
                fallback: "cleaning",
                options: [
                  { value: "cleaning", label: "Cleaning" },
                  { value: "property-management", label: "Property management" },
                ],
              }
            : {
                field: "advertiserType",
                allLabel: "Anyone advertising",
                fallback: "unknown",
                options: [
                  { value: "individual", label: "Individuals only" },
                  { value: "company", label: "Companies only" },
                  { value: "unknown", label: "Unclear" },
                ],
              }
        }
        emptyHint={
          demand
            ? "Businesses that might need cleaning. Use Find more leads to search prospects, public tenders and building permits."
            : "Cleaners who might work with you. Use Find more leads to search Kijiji and Google."
        }
        rowActions={(row) => {
          const converted = row["convertedTo"] as { type?: string } | undefined;
          if (converted) {
            return (
              <span className="muted" style={{ fontSize: "0.82rem", marginRight: "0.4rem" }}>
                → {converted.type === "CLEANER" ? "Cleaner" : "Contract"}
              </span>
            );
          }
          return (
            <button className="btn btn--sm btn--ghost" onClick={() => setConverting(row)}>
              {demand ? "Make contract" : "Add as cleaner"}
            </button>
          );
        }}
      />

      {converting && (
        <ConvertDialog lead={converting} onClose={() => setConverting(null)} />
      )}
    </>
  );
}

/**
 * Turns a lead into the record it was heading towards.
 *
 * A supply lead already carries everything a cleaner needs, so that side is a
 * confirmation. The contract side asks for terms, but does not insist: "we won
 * them, the rate is still to settle" is a real state, and DRAFT says so.
 */
function ConvertDialog({ lead, onClose }: { lead: Record_; onClose: () => void }) {
  const qc = useQueryClient();
  const supply = lead["side"] === "supply";
  const target = supply ? "cleaner" : "contract";

  const convert = useMutation({
    mutationFn: (fields: Record_) =>
      api.create<Record_>(`leads/${String(lead["id"])}/convert`, { target, fields }),
    onSuccess: () => {
      for (const key of ["leads", "cleaners", "contracts", "summary"]) {
        void qc.invalidateQueries({ queryKey: [key] });
      }
      onClose();
    },
  });

  return (
    <div className="form-card">
      <h2 className="page-subhead" style={{ marginTop: 0 }}>
        {supply
          ? `Add ${String(lead["name"])} as a cleaner`
          : `Create a contract with ${String(lead["name"])}`}
      </h2>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          const data = new FormData(e.currentTarget);
          const values: Record_ = {};

          for (const [key, raw] of data.entries()) {
            const value = String(raw).trim();
            if (!value) continue;
            if (key === "rate") values[key] = Number(value);
            else if (key === "coverageArea" || key === "capabilities") {
              values[key] = value.split(",").map((s) => s.trim()).filter(Boolean);
            } else values[key] = value;
          }

          convert.mutate(values);
        }}
      >
        <div className="form-grid">
          {supply ? (
            <>
              <label className="field">
                <span className="field__label">Coverage area</span>
                <input
                  name="coverageArea"
                  defaultValue={String(lead["address"] ?? "")}
                  placeholder="Barrie, Orillia"
                />
                <span className="field__help">Comma-separated</span>
              </label>
              <label className="field">
                <span className="field__label">Capabilities</span>
                <input name="capabilities" placeholder="residential, commercial" />
                <span className="field__help">Comma-separated</span>
              </label>
            </>
          ) : (
            <>
              <label className="field">
                <span className="field__label">Site address</span>
                <input name="siteAddress" defaultValue={String(lead["address"] ?? "")} />
              </label>
              <label className="field">
                <span className="field__label">Rate</span>
                <input name="rate" type="number" step="0.01" min="0" />
              </label>
              <label className="field">
                <span className="field__label">Per</span>
                <select name="rateUnit" defaultValue="visit">
                  {RATE_UNITS.map((u) => (
                    <option key={u} value={u}>
                      {u}
                    </option>
                  ))}
                </select>
              </label>
              <label className="field">
                <span className="field__label">Schedule</span>
                <input name="schedule" placeholder="Weekly, Tuesday evenings" />
              </label>
              <label className="field">
                <span className="field__label">Start date</span>
                <input name="startDate" type="date" />
                <span className="field__help">Leave the terms blank if they aren't settled yet.</span>
              </label>
              <label className="field field--wide">
                <span className="field__label">Scope</span>
                <textarea
                  name="scope"
                  rows={3}
                  defaultValue={String(lead["description"] ?? lead["pitch"] ?? "")}
                />
              </label>
            </>
          )}
        </div>

        {convert.error != null && <ErrorNote error={convert.error} />}

        <div className="form-card__actions">
          <button className="btn btn--primary" type="submit" disabled={convert.isPending}>
            {convert.isPending
              ? "Converting…"
              : supply
                ? "Add as cleaner"
                : "Create contract"}
          </button>
          <button className="btn btn--ghost" type="button" onClick={onClose}>
            Cancel
          </button>
        </div>
      </form>

      <p className="muted" style={{ fontSize: "0.85rem" }}>
        The lead is marked won and linked to the new record.
      </p>
    </div>
  );
}
